import type { Locale } from '@xphoto/shared';

/**
 * Formatage d'affichage pour la visionneuse (tailles, dates, durées, EXIF).
 * Sans dépendance : `Intl` pour dates/nombres, localisé par la langue courante.
 */

/** Taille lisible (Kio/Mio/Gio, base 1024). `bytes` entier ≥ 0. */
export function formatBytes(bytes: number, locale: Locale): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KiB', 'MiB', 'GiB', 'TiB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const nf = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  return `${nf.format(value)} ${units[unit]}`;
}

/** Date+heure absolue localisée à partir d'un ISO 8601, ou `null` si absente/invalide. */
export function formatDateTime(iso: string | null, locale: Locale): string | null {
  if (iso === null) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
}

/**
 * Portion RÉPERTOIRE (dossier) d'un chemin relatif POSIX, sans le nom de fichier.
 * Ex. `2024/vacances/img.jpg` → `2024/vacances` ; `img.jpg` (racine) → `''`.
 * Tolère les slashs de tête/queue en trop. Retourne `''` pour un fichier à la racine.
 */
export function dirnameOf(relPath: string): string {
  const trimmed = relPath.replace(/\/+$/, '');
  const idx = trimmed.lastIndexOf('/');
  if (idx <= 0) return '';
  return trimmed.slice(0, idx);
}

/** Durée `mm:ss` (ou `h:mm:ss`) à partir de secondes. */
export function formatDuration(seconds: number): string {
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/**
 * Champs EXIF « chauds » sélectionnés pour l'affichage, dans un ordre lisible.
 * Le snapshot ExifTool est vaste et hétérogène : on n'expose qu'un sous-ensemble
 * connu (appareil, objectif, exposition, ISO, focale), formaté simplement.
 * Retourne des paires `{ label, value }` (label = clé EXIF brute, valeur = string).
 *
 * Le GPS est HORS de cette liste : les paires brutes `GPSLatitude`/`GPSLongitude` sont
 * trompeuses (magnitude NON SIGNÉE, cf. `gpsFromExif`) et leur `*Ref` isolé
 * (`"N"`/`"S"`/`"E"`/`"W"`) n'a aucun sens pour un lecteur. Le GPS est donc rendu à
 * part, en une ligne dédiée, à partir de `gpsFromExif` — les `*Ref` ne doivent JAMAIS
 * réapparaître ici.
 */
const EXIF_FIELDS: readonly string[] = [
  'Make',
  'Model',
  'LensModel',
  'FNumber',
  'ExposureTime',
  'ISO',
  'FocalLength',
  'Flash',
  'Orientation',
  'Software',
  'Artist',
  'Copyright',
];

export interface ExifPair {
  key: string;
  value: string;
}

/** Extrait les paires EXIF affichables du snapshot brut (ignore les valeurs vides). */
export function selectExif(snapshot: Record<string, unknown> | null): ExifPair[] {
  if (snapshot === null) return [];
  const pairs: ExifPair[] = [];
  for (const key of EXIF_FIELDS) {
    const raw = snapshot[key];
    if (raw === undefined || raw === null || raw === '') continue;
    if (typeof raw === 'object') continue;
    pairs.push({ key, value: String(raw) });
  }
  return pairs;
}

/**
 * Champ après lequel la ligne GPS s'insère : la place qu'occupait `GPSLatitude` du temps
 * où le GPS sortait de `EXIF_FIELDS` (entre `Orientation` et `Software`). Le GPS est
 * désormais rendu à part (cf. `gpsFromExif`) mais garde sa place VISIBLE — l'ordre de la
 * section « Métadonnées EXIF » ne bouge pas pour l'utilisateur.
 */
const GPS_AFTER_FIELD = 'Orientation';

/**
 * Index d'INSERTION de la ligne GPS dans les paires rendues par `selectExif`.
 *
 * `selectExif` respecte l'ordre d'`EXIF_FIELDS` : il suffit de compter les paires qui
 * précèdent la place historique du GPS. Dérivé d'`EXIF_FIELDS` (aucun ordre dupliqué
 * ailleurs) → réordonner `EXIF_FIELDS` garde la ligne GPS au bon endroit. Les paires
 * absentes du snapshot ne comptent pas : un snapshot sans `Orientation` place donc le
 * GPS juste après le dernier champ présent qui le précède. Fonction PURE.
 */
export function gpsInsertIndex(pairs: readonly ExifPair[]): number {
  const gpsRank = EXIF_FIELDS.indexOf(GPS_AFTER_FIELD);
  return pairs.filter((pair) => EXIF_FIELDS.indexOf(pair.key) <= gpsRank).length;
}

/**
 * ─── GPS ──────────────────────────────────────────────────────────────────────
 *
 * Coordonnées décimales SIGNÉES (convention usuelle : Nord/Est positifs, Sud/Ouest
 * négatifs) — la seule forme affichable et liable telle quelle.
 *
 * POURQUOI un helper plutôt que les paires brutes : le snapshot n'est PAS fiable sur le
 * signe. L'extraction API (`apps/api/src/scan/exif.ts`) lit ExifTool en `-n -G0` et
 * `flattenGroupedRecord` garde la PREMIÈRE occurrence de chaque tag → `EXIF:GPSLatitude`
 * (magnitude NON signée) l'emporte sur `Composite:GPSLatitude` (le seul qui applique le
 * signe depuis le Ref). Le signe vit alors dans `GPSLatitudeRef`/`GPSLongitudeRef`
 * (`"N"`/`"S"`, `"E"`/`"W"`), présents dans le snapshot. Sans recomposition, une photo de
 * l'hémisphère SUD ou à l'OUEST est affichée/liée au mauvais endroit (bug corrigé ici).
 *
 * On ne suppose RIEN de la forme reçue : on prend la MAGNITUDE (`Math.abs`) puis on
 * repose le signe depuis le Ref → correct que le snapshot soit déjà signé ou non
 * (anciens snapshots, autre chaîne d'extraction, futur correctif côté API).
 */

/** Coordonnées GPS décimales SIGNÉES, prêtes à afficher et à lier. */
export interface GpsCoords {
  /** Latitude signée, dans [-90, 90] (négatif = hémisphère Sud). */
  lat: number;
  /** Longitude signée, dans [-180, 180] (négatif = Ouest). */
  lon: number;
}

/**
 * Nombre FINI depuis une valeur de snapshot (`Record<string, unknown>` : on ne fait
 * aucune hypothèse — `-n` donne un `number`, une autre lecture donnerait une chaîne).
 * Rejette `NaN`/`Infinity`, les non-numériques et tout ce qui n'est ni `number` ni
 * `string`. Retourne `null` si inexploitable.
 */
function coordNumber(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  // `Number('')` vaut 0 : une chaîne vide n'est PAS une coordonnée au point zéro.
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Repose le signe d'une coordonnée depuis son Ref (`GPSLatitudeRef`/`GPSLongitudeRef`).
 * Comparaison sur l'INITIALE, insensible à la casse et aux espaces (`-n` donne `"S"`,
 * une lecture sans `-n` donnerait `"South"`). Ref absent, vide ou NON RECONNU → valeur
 * rendue TELLE QUELLE, signe compris : on ne devine pas.
 */
function applyGpsRef(value: number, rawRef: unknown, negative: string, positive: string): number {
  if (typeof rawRef !== 'string') return value;
  const initial = rawRef.trim().toUpperCase().charAt(0);
  if (initial === negative) return -Math.abs(value);
  if (initial === positive) return Math.abs(value);
  return value;
}

/**
 * Coordonnées GPS décimales SIGNÉES d'un snapshot EXIF, ou `null` si indisponibles.
 *
 * `null` dès que le résultat ne serait ni affichable ni liable : snapshot absent, coordonnée
 * non numérique/non finie, hors bornes (|lat| > 90, |lon| > 180), ou UNE SEULE des deux
 * présentes (une demi-coordonnée ne situe rien). Fonction PURE.
 */
export function gpsFromExif(snapshot: Record<string, unknown> | null): GpsCoords | null {
  if (snapshot === null) return null;
  const rawLat = coordNumber(snapshot.GPSLatitude);
  const rawLon = coordNumber(snapshot.GPSLongitude);
  if (rawLat === null || rawLon === null) return null;
  const lat = applyGpsRef(rawLat, snapshot.GPSLatitudeRef, 'S', 'N');
  const lon = applyGpsRef(rawLon, snapshot.GPSLongitudeRef, 'W', 'E');
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { lat, lon };
}

/** Décimales d'AFFICHAGE des coordonnées (~1 m ; le lien garde la précision pleine). */
const GPS_DISPLAY_DECIMALS = 5;

/**
 * Coordonnées ARRONDIES pour l'affichage (ex. `46.023 / 7.749`). Zéros de queue retirés.
 *
 * Séparateur ` / ` (slash) et NON la virgule : la ligne est libellée « GPS lat/long »
 * (`info.gps`) — le slash y répond directement et lève l'ambiguïté avec la virgule
 * décimale des locales FR/DE. Point décimal NON localisé : c'est la notation universelle
 * des coordonnées, cohérente avec le lien Google Maps porté par la même valeur.
 */
export function formatGpsCoords(coords: GpsCoords): string {
  const round = (value: number): string => String(Number(value.toFixed(GPS_DISPLAY_DECIMALS)));
  return `${round(coords.lat)} / ${round(coords.lon)}`;
}

/**
 * Lien Google Maps vers des coordonnées, au format officiel « Maps URLs » :
 * `https://www.google.com/maps/search/?api=1&query=<lat>,<lon>`, en PLEINE précision
 * (l'arrondi ne concerne que l'affichage). La query est encodée par `URLSearchParams`
 * (la virgule sort en `%2C`, forme encodée acceptée par Google) → aucune valeur
 * concaténée brute dans l'URL.
 */
export function googleMapsUrl(coords: GpsCoords): string {
  const query = new URLSearchParams({ api: '1', query: `${coords.lat},${coords.lon}` });
  return `https://www.google.com/maps/search/?${query.toString()}`;
}
